import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { colors } from "../colors";
import axiosInstance from "../axiosInstance";

const EditProfileModal = ({ onClose, user, onProfileUpdate }) => {
  const [activeTab, setActiveTab] = useState("details"); // details, email, password
  const [selectedCountryCode, setSelectedCountryCode] = useState("+44");
  const [formData, setFormData] = useState({
    name: user?.name || "",
    phone_number: user?.phone_number || "",
    street_address: user?.street_address || "",
    city: user?.city || "",
    county: user?.county || "",
    postal_code: user?.postal_code || "",
  });

  const [emailData, setEmailData] = useState({
    newEmail: "",
    otp: "",
  });

  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [otpSent, setOtpSent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [passwordValidations, setPasswordValidations] = useState({
    hasCapital: false,
    hasNumber: false,
    isLongEnough: false,
    passwordsMatch: true,
  });

  const countryCodes = [{ code: "+44", country: "UK" }];

  const HeadingTextStyle = {
    fontFamily: "Bambino",
    fontWeight: 650,
  };

  const NameTextStyle = {
    fontFamily: "Bambino",
    fontWeight: 450,
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      const hasCapital = /[A-Z]/.test(passwordData.newPassword);
      const hasNumber = /\d/.test(passwordData.newPassword);
      const isLongEnough = passwordData.newPassword.length > 8;
      const passwordsMatch =
        passwordData.newPassword === passwordData.confirmPassword;

      setPasswordValidations({
        hasCapital,
        hasNumber,
        isLongEnough,
        passwordsMatch,
      });
    }, 100);

    return () => clearTimeout(timeout);
  }, [passwordData.newPassword, passwordData.confirmPassword]);

  const formatPhoneNumber = (value) => {
    const number = value.replace(/\D/g, "");
    if (number.length <= 4) {
      return number;
    } else if (number.length <= 7) {
      return `${number.slice(0, 4)} ${number.slice(4)}`;
    } else {
      return `${number.slice(0, 4)} ${number.slice(4, 7)} ${number.slice(
        7,
        11
      )}`;
    }
  };

  const handlePhoneChange = (e) => {
    const formattedNumber = formatPhoneNumber(e.target.value);
    setFormData({ ...formData, phone_number: formattedNumber });
  };

  const handleChange = (e, dataType) => {
    const { name, value } = e.target;
    if (dataType === "details") {
      setFormData({ ...formData, [name]: value });
    } else if (dataType === "email") {
      setEmailData({ ...emailData, [name]: value });
    } else if (dataType === "password") {
      setPasswordData({ ...passwordData, [name]: value });
    }
  };

  // Update Profile Details
  const handleUpdateDetails = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axiosInstance.put(
        "/auth/update-profile",
        formData,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Profile updated successfully!");
        onProfileUpdate(response.data.user);
        setTimeout(() => onClose(), 2000);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update profile"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Send OTP for Email Change
  const handleSendOTP = async () => {
    if (!emailData.newEmail) {
      toast.error("Please enter a new email address");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.post(
        "/auth/send-email-otp",
        { newEmail: emailData.newEmail },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("OTP sent to your new email!");
        setOtpSent(true);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to send OTP");
    } finally {
      setIsLoading(false);
    }
  };

  // Verify OTP and Update Email
  const handleUpdateEmail = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axiosInstance.put(
        "/auth/update-email",
        { newEmail: emailData.newEmail, otp: emailData.otp },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Email updated successfully!");
        onProfileUpdate(response.data.user);
        setTimeout(() => onClose(), 2000);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update email");
    } finally {
      setIsLoading(false);
    }
  };

  // Update Password
  const handleUpdatePassword = async (e) => {
    e.preventDefault();

    if (!passwordValidations.hasCapital || !passwordValidations.hasNumber || !passwordValidations.isLongEnough) {
      toast.error("Please meet all password requirements");
      return;
    }

    if (!passwordValidations.passwordsMatch) {
      toast.error("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const response = await axiosInstance.put(
        "/auth/update-password",
        {
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        },
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success("Password updated successfully!");
        setTimeout(() => onClose(), 2000);
      }
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update password"
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
        style={{ zIndex: 200 }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.8 }}
          animate={{ scale: 1 }}
          exit={{ scale: 0.8 }}
          transition={{ duration: 0.3 }}
          className="bg-white p-6 rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex justify-between items-center mb-6">
            <h2
              className="text-xl font-bold"
              style={HeadingTextStyle}
            >
              Edit Profile
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b mb-6">
            <button
              onClick={() => setActiveTab("details")}
              className={`px-4 py-2 font-medium ${
                activeTab === "details"
                  ? "border-b-2 text-blue-600"
                  : "text-gray-500"
              }`}
              style={{
                borderColor: activeTab === "details" ? colors.primaryGreen : "transparent",
                color: activeTab === "details" ? colors.primaryGreen : "#6B7280",
              }}
            >
              Profile Details
            </button>
            <button
              onClick={() => setActiveTab("email")}
              className={`px-4 py-2 font-medium ${
                activeTab === "email"
                  ? "border-b-2 text-blue-600"
                  : "text-gray-500"
              }`}
              style={{
                borderColor: activeTab === "email" ? colors.primaryGreen : "transparent",
                color: activeTab === "email" ? colors.primaryGreen : "#6B7280",
              }}
            >
              Change Email
            </button>
            <button
              onClick={() => setActiveTab("password")}
              className={`px-4 py-2 font-medium ${
                activeTab === "password"
                  ? "border-b-2 text-blue-600"
                  : "text-gray-500"
              }`}
              style={{
                borderColor: activeTab === "password" ? colors.primaryGreen : "transparent",
                color: activeTab === "password" ? colors.primaryGreen : "#6B7280",
              }}
            >
              Change Password
            </button>
          </div>

          {/* Profile Details Tab */}
          {activeTab === "details" && (
            <form onSubmit={handleUpdateDetails}>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={NameTextStyle}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={(e) => handleChange(e, "details")}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Enter your full name"
                    maxLength="32"
                    required
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={NameTextStyle}
                  >
                    Phone Number
                  </label>
                  <div className="flex">
                    <select
                      value={selectedCountryCode}
                      onChange={(e) => setSelectedCountryCode(e.target.value)}
                      className="p-2 border border-gray-300 text-sm rounded-l w-24 bg-gray-50"
                    >
                      {countryCodes.map((country) => (
                        <option key={country.code} value={country.code}>
                          {country.code} {country.country}
                        </option>
                      ))}
                    </select>
                    <input
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handlePhoneChange}
                      className="w-full p-2 border border-gray-300 rounded-r text-sm"
                      placeholder="7XXX XXX XXX"
                      maxLength="13"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={NameTextStyle}
                  >
                    Street Address
                  </label>
                  <input
                    type="text"
                    name="street_address"
                    value={formData.street_address}
                    onChange={(e) => handleChange(e, "details")}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Enter your street address"
                    required
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={NameTextStyle}
                  >
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={(e) => handleChange(e, "details")}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Enter your city"
                    required
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={NameTextStyle}
                  >
                    County
                  </label>
                  <input
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={(e) => handleChange(e, "details")}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Enter your county"
                    required
                  />
                </div>

                <div>
                  <label
                    className="block text-sm font-medium mb-2"
                    style={NameTextStyle}
                  >
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => handleChange(e, "details")}
                    className="w-full p-2 border border-gray-300 rounded text-sm"
                    placeholder="Enter your postal code"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-white p-2 rounded hover:opacity-90 text-sm"
                style={{ backgroundColor: colors.primaryGreen }}
              >
                {isLoading ? "Updating..." : "Update Profile"}
              </button>
            </form>
          )}

          {/* Change Email Tab */}
          {activeTab === "email" && (
            <form onSubmit={handleUpdateEmail}>
              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={NameTextStyle}
                >
                  Current Email
                </label>
                <input
                  type="email"
                  value={user?.email || ""}
                  className="w-full p-2 border border-gray-300 rounded text-sm bg-gray-100"
                  disabled
                />
              </div>

              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={NameTextStyle}
                >
                  New Email
                </label>
                <div className="flex gap-2">
                  <input
                    type="email"
                    name="newEmail"
                    value={emailData.newEmail}
                    onChange={(e) => handleChange(e, "email")}
                    className="flex-1 p-2 border border-gray-300 rounded text-sm"
                    placeholder="Enter new email"
                    required
                    disabled={otpSent}
                  />
                  {!otpSent && (
                    <button
                      type="button"
                      onClick={handleSendOTP}
                      disabled={isLoading}
                      className="px-4 py-2 text-white rounded text-sm"
                      style={{ backgroundColor: colors.primaryGreen }}
                    >
                      Send OTP
                    </button>
                  )}
                </div>
              </div>

              {otpSent && (
                <>
                  <div className="mb-4">
                    <label
                      className="block text-sm font-medium mb-2"
                      style={NameTextStyle}
                    >
                      Enter OTP
                    </label>
                    <input
                      type="text"
                      name="otp"
                      value={emailData.otp}
                      onChange={(e) => handleChange(e, "email")}
                      className="w-full p-2 border border-gray-300 rounded text-sm"
                      placeholder="Enter 6-digit OTP"
                      maxLength="6"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full text-white p-2 rounded hover:opacity-90 text-sm"
                    style={{ backgroundColor: colors.primaryGreen }}
                  >
                    {isLoading ? "Verifying..." : "Verify & Update Email"}
                  </button>
                </>
              )}
            </form>
          )}

          {/* Change Password Tab */}
          {activeTab === "password" && (
            <form onSubmit={handleUpdatePassword}>
              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={NameTextStyle}
                >
                  Old Password
                </label>
                <input
                  type="password"
                  name="oldPassword"
                  value={passwordData.oldPassword}
                  onChange={(e) => handleChange(e, "password")}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  placeholder="Enter your old password"
                  required
                />
              </div>

              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={NameTextStyle}
                >
                  New Password
                </label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => handleChange(e, "password")}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  placeholder="Enter your new password"
                  required
                />
                <div className="text-sm mt-2">
                  <p
                    style={{
                      color: passwordValidations.hasCapital ? "green" : "red",
                    }}
                  >
                    • At least one capital letter
                  </p>
                  <p
                    style={{
                      color: passwordValidations.hasNumber ? "green" : "red",
                    }}
                  >
                    • At least one number
                  </p>
                  <p
                    style={{
                      color: passwordValidations.isLongEnough ? "green" : "red",
                    }}
                  >
                    • Longer than 8 characters
                  </p>
                </div>
              </div>

              <div className="mb-4">
                <label
                  className="block text-sm font-medium mb-2"
                  style={NameTextStyle}
                >
                  Confirm New Password
                </label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handleChange(e, "password")}
                  className="w-full p-2 border border-gray-300 rounded text-sm"
                  placeholder="Confirm your new password"
                  required
                />
                {passwordData.confirmPassword &&
                  !passwordValidations.passwordsMatch && (
                    <p className="text-red-500 text-sm mt-1">
                      • Passwords do not match
                    </p>
                  )}
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full text-white p-2 rounded hover:opacity-90 text-sm"
                style={{ backgroundColor: colors.primaryGreen }}
              >
                {isLoading ? "Updating..." : "Update Password"}
              </button>
            </form>
          )}
        </motion.div>
      </motion.div>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        newestOnTop
      />
    </AnimatePresence>
  );
};

export default EditProfileModal;